import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  HeartIcon,
  PhoneIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  ClockIcon,
  MapPinIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useContactInfo, useBranding } from '../hooks/useHospitalConfig';

export const MedicalAssistancePage: React.FC = () => {
  const navigate = useNavigate();
  const { emergency, general, loading: contactLoading } = useContactInfo();
  const { hospitalName } = useBranding();

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <button 
              onClick={() => navigate('/onecare/')}
              className="flex items-center group"
            >
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-gradient-to-r from-red-600 to-pink-600 rounded-lg flex items-center justify-center">
                  <HeartIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900 group-hover:text-red-600 transition-colors">
                  {hospitalName || 'Aimepic OneCare'}
                </h1>
                <p className="text-sm text-gray-800">Medical Assistance</p>
              </div>
            </button>
            
            <nav className="flex space-x-8">
              <button 
                onClick={() => navigate('/onecare/patients')}
                className="flex items-center text-gray-900 hover:text-red-600 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Options
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4">
            Medical Assistance
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-pink-600">
              Immediate Help Available
            </span>
          </h1>
          <p className="text-xl text-gray-900 max-w-3xl mx-auto">
            If you need urgent medical attention or emergency care, we're here to help 24/7.
          </p>
        </div>

        {/* Emergency Alert */}
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 mb-12">
          <div className="flex items-center mb-4">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600 mr-4" />
            <h2 className="text-2xl font-bold text-red-900">Medical Emergency</h2>
          </div>
          <p className="text-red-800 text-lg mb-6">
            If you are experiencing a life-threatening emergency, do not wait. Call for immediate assistance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="tel:911"
              className="flex items-center justify-center bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 rounded-lg transition-colors duration-300 text-lg"
            >
              <PhoneIcon className="h-6 w-6 mr-3" />
              Call 911 - Emergency
            </a>
            {emergency ? (
              <a
                href={`tel:${emergency.phone}`}
                className="flex items-center justify-center bg-red-500 hover:bg-red-600 text-white font-bold py-4 px-8 rounded-lg transition-colors duration-300 text-lg"
              >
                <PhoneIcon className="h-6 w-6 mr-3" />
                Call {emergency.phone_display}
              </a>
            ) : (
              <div className="flex items-center justify-center bg-gray-300 text-gray-600 font-bold py-4 px-8 rounded-lg text-lg">
                <PhoneIcon className="h-6 w-6 mr-3" />
                Loading emergency contact...
              </div>
            )}
          </div>
        </div>

        {/* Contact Options */}
        <div className="grid gap-8 mb-12">
          {/* Emergency Department */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                <ExclamationTriangleIcon className="w-8 h-8 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                  Emergency Department
                </h3>
                <p className="text-gray-900 text-base leading-relaxed mb-6">
                  Our Emergency Department is staffed 24/7 with board-certified emergency physicians and trauma specialists. 
                  We handle all types of medical emergencies and urgent care needs.
                </p>
                
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <ClockIcon className="h-5 w-5 text-red-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Hours</p>
                      <p className="text-sm text-gray-900">24/7 - Always Open</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <PhoneIcon className="h-5 w-5 text-red-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Emergency Line</p>
                      <p className="text-sm text-gray-900">{emergency?.phone_display || 'Loading...'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <MapPinIcon className="h-5 w-5 text-red-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Location</p>
                      <p className="text-sm text-gray-900">Main Building Entrance</p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-2">When to visit Emergency:</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    <li>• Chest pain or difficulty breathing</li>
                    <li>• Severe injuries or trauma</li>
                    <li>• Signs of stroke or heart attack</li>
                    <li>• Severe allergic reactions</li>
                    <li>• Any life-threatening condition</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Urgent Care */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-2xl flex items-center justify-center shadow-lg">
                <UserGroupIcon className="w-8 h-8 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                  Urgent Care Services
                </h3>
                <p className="text-gray-900 text-base leading-relaxed mb-6">
                  For non-emergency medical needs that require prompt attention. Our urgent care team provides 
                  same-day treatment for minor injuries and illnesses.
                </p>
                
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <ClockIcon className="h-5 w-5 text-orange-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Hours</p>
                      <p className="text-sm text-gray-900">Mon-Sun: 8AM-10PM</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <PhoneIcon className="h-5 w-5 text-orange-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Urgent Care</p>
                      <p className="text-sm text-gray-900">(555) 123-URGENT</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <MapPinIcon className="h-5 w-5 text-orange-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Location</p>
                      <p className="text-sm text-gray-900">Urgent Care Wing - East</p>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-800 mb-2">Urgent Care treats:</h4>
                  <ul className="text-sm text-orange-700 space-y-1">
                    <li>• Minor cuts and burns</li>
                    <li>• Sprains and minor fractures</li>
                    <li>• Cold and flu symptoms</li>
                    <li>• Minor infections</li>
                    <li>• Non-emergency issues needing same-day care</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* When in doubt */}
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Not sure if it's an emergency?
          </h3>
          <p className="text-gray-900 text-lg mb-6">
            When in doubt, it's always better to seek immediate medical attention. 
            Our medical professionals are available to help assess your situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {emergency ? (
              <a
                href={`tel:${emergency.phone}`}
                className="flex items-center justify-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
              >
                <PhoneIcon className="h-5 w-5 mr-3" />
                Call Our Medical Team
              </a>
            ) : (
              <div className="flex items-center justify-center bg-gray-300 text-gray-600 font-semibold py-3 px-6 rounded-lg">
                <PhoneIcon className="h-5 w-5 mr-3" />
                Loading...
              </div>
            )}
            <button
              onClick={() => navigate('/onecare/patients/encounter')}
              className="flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
            >
              Contact Hospital Staff
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-20">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-800">
              <HeartIcon className="h-4 w-4 mr-2" />
              <span>HIPAA-Ready & Secure</span>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-800">
              <button
                onClick={() => window.open('/patient/privacy', '_blank')}
                className="hover:text-red-600 transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => window.open('/patient/help', '_blank')}
                className="hover:text-red-600 transition-colors"
              >
                Help & Support
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MedicalAssistancePage;